/**
 * AdminContent.jsx — Content Management System
 *
 * Sections:
 *   📣 Testimonials  — add / edit / delete customer quotes
 *   🏭 Use Cases     — industry problem→solution cards
 *   🚀 Hero          — homepage hero copy
 *   📦 Products      — categories, tags, materials
 *
 * Language toggle: IT / EN.
 * Loads from MongoDB via contentAPI; pre-populates from i18n
 * translation files when DB is empty so the founder never starts blank.
 * Each item saves independently; unsaved changes are highlighted amber.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { useToast } from '../../contexts/ToastContext';
import { contentAPI } from '../../services/api';
import {
  Plus, Trash2, Save, RefreshCw,
  MessageSquareQuote, Factory, Rocket, Package, Globe, X,
} from 'lucide-react';
import '../Admin/AdminDashboard.css';
import './AdminContent.css';

import enT from '../../locales/en/translation.json';
import itT from '../../locales/it/translation.json';

// ── helpers ───────────────────────────────────────────────────────

const uid = () => `new_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

const SECTIONS = [
  { key: 'testimonials', label: 'Testimonials', Icon: MessageSquareQuote, color: '#ec4899' },
  { key: 'useCases',     label: 'Use Cases',    Icon: Factory,             color: '#06b6d4' },
  { key: 'hero',         label: 'Hero',         Icon: Rocket,              color: '#f59e0b' },
  { key: 'products',     label: 'Products',     Icon: Package,             color: '#10b981' },
];

// ── seed from i18n defaults when DB has no data ───────────────────

function seedTestimonials(lang) {
  const src = lang === 'en' ? enT : itT;
  return (src.testimonials || []).map((item, i) => ({
    _localId: uid(), _dbId: null, isDirty: true,
    key: `t_${i}`,
    data: { avatar: item.avatar || '😊', name: item.name || '', role: item.role || '', quote: item.quote || '', label: item.label || '' },
  }));
}

function seedUseCases(lang) {
  const src = lang === 'en' ? enT : itT;
  const cases = src.useCases?.cases || [];
  return cases.map((item, i) => ({
    _localId: uid(), _dbId: null, isDirty: true,
    key: `uc_${i}`,
    data: { icon: item.icon || '🏭', title: item.title || '', problem: item.problem || '', solution: item.solution || '' },
  }));
}

function seedHero(lang) {
  const h = (lang === 'en' ? enT : itT).hero || {};
  return {
    _localId: uid(), _dbId: null, isDirty: true,
    key: 'hero_main',
    data: {
      badge: h.badge || '', title1: h.title1 || '', title2: h.title2 || '',
      subtitle: h.subtitle || '', subtitleBold: h.subtitleBold || '',
      ctaPrimary: h.ctaPrimary || '', ctaSecondary: h.ctaSecondary || '',
      chip1val: h.chip1val || '24h', trust1: h.trust1 || '',
      chip2val: h.chip2val || '1',   trust2: h.trust2 || '',
      chip3val: h.chip3val || '2h',  trust3: h.trust3 || '',
    },
  };
}

function seedProducts(lang) {
  const p = (lang === 'en' ? enT : itT).products || {};
  return {
    _localId: uid(), _dbId: null, isDirty: true,
    key: 'products_main',
    data: {
      label: p.label || '', title: p.title || '', subtitle: p.subtitle || '',
      tags: [...(p.tags || [])],
      materialsList: [...(p.materialsList || [])],
      categories: (p.categories || []).map(c => ({ ...c })),
    },
  };
}

function dbToLocal(items) {
  return items.map(item => ({
    _localId: uid(), _dbId: item._id, isDirty: false,
    key: item.key, data: { ...item.data },
  }));
}

// ── component ─────────────────────────────────────────────────────

const AdminContent = () => {
  const toast        = useToast();
  const [lang, setLang]       = useState('it');
  const [section, setSection] = useState('testimonials');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState({});   // { localId: true }

  // per-section state: { it: [...], en: [...] }  (null = not loaded)
  const [testimonials, setTestimonials] = useState({ it: null, en: null });
  const [useCases,     setUseCases    ] = useState({ it: null, en: null });
  const [hero,         setHero        ] = useState({ it: null, en: null }); // single item each lang
  const [products,     setProducts    ] = useState({ it: null, en: null }); // single item each lang

  // add-row state
  const [newTag, setNewTag] = useState('');
  const [newMat, setNewMat] = useState('');
  const [newCat, setNewCat] = useState({ name: '', desc: '' });

  const loadedRef = useRef({}); // { 'testimonials:it': true }

  // ── load ──────────────────────────────────────────────────────

  const loadSection = useCallback(async (sec, lng) => {
    const key = `${sec}:${lng}`;
    if (loadedRef.current[key]) return;
    loadedRef.current[key] = true;
    try {
      const res = await contentAPI.getAll({ section: sec, language: lng });
      const items = res.data?.content || [];
      // Never seed from i18n defaults when the DB returns an empty result —
      // that would ghost back deleted content on every refresh. Seed only on
      // network/API errors so the UI stays usable when the backend is down.
      if (sec === 'testimonials') {
        setTestimonials(p => ({ ...p, [lng]: dbToLocal(items) }));
      } else if (sec === 'useCases') {
        setUseCases(p => ({ ...p, [lng]: dbToLocal(items) }));
      } else if (sec === 'hero') {
        // Use false (not null) to represent "loaded but empty" so the render
        // can distinguish this from null = "not yet fetched"
        setHero(p => ({ ...p, [lng]: items.length ? dbToLocal(items)[0] : false }));
      } else if (sec === 'products') {
        setProducts(p => ({ ...p, [lng]: items.length ? dbToLocal(items)[0] : false }));
      }
    } catch (err) {
      console.error(`[content] load ${sec}/${lng}`, err);
      // Seed only on API error so the editor isn't completely blank
      if (sec === 'testimonials') setTestimonials(p => ({ ...p, [lng]: seedTestimonials(lng) }));
      else if (sec === 'useCases') setUseCases(p => ({ ...p, [lng]: seedUseCases(lng) }));
      else if (sec === 'hero')     setHero(p => ({ ...p, [lng]: seedHero(lng) }));
      else if (sec === 'products') setProducts(p => ({ ...p, [lng]: seedProducts(lng) }));
    }
  }, []);

  useEffect(() => { loadSection(section, lang); }, [section, lang, loadSection]);

  const forceReload = () => {
    loadedRef.current[`${section}:${lang}`] = false;
    setLoading(true);
    loadSection(section, lang).finally(() => setLoading(false));
  };

  // ── save / delete ─────────────────────────────────────────────

  const saveItem = async (sec, lng, item) => {
    setSaving(p => ({ ...p, [item._localId]: true }));
    try {
      const res = await contentAPI.createOrUpdate({ section: sec, language: lng, key: item.key, data: item.data });
      const saved = res.data?.content;
      const applyToList = setter => setter(p => ({
        ...p,
        [lng]: p[lng].map(x => x._localId === item._localId
          ? { ...x, _dbId: saved?._id || x._dbId, isDirty: false }
          : x),
      }));
      const applyToSingle = setter => setter(p => ({
        ...p,
        [lng]: { ...p[lng], _dbId: saved?._id || p[lng]._dbId, isDirty: false },
      }));

      if (sec === 'testimonials') applyToList(setTestimonials);
      else if (sec === 'useCases') applyToList(setUseCases);
      else if (sec === 'hero')     applyToSingle(setHero);
      else if (sec === 'products') applyToSingle(setProducts);

      toast.success('Saved ✓');
    } catch (err) {
      toast.error(`Save failed: ${err?.response?.data?.message || err.message}`);
    } finally {
      setSaving(p => ({ ...p, [item._localId]: false }));
    }
  };

  const deleteItem = async (sec, lng, item) => {
    if (!item._dbId) {
      const remove = setter => setter(p => ({ ...p, [lng]: p[lng].filter(x => x._localId !== item._localId) }));
      if (sec === 'testimonials') remove(setTestimonials);
      else if (sec === 'useCases') remove(setUseCases);
      return;
    }
    if (!window.confirm('Delete this item? This cannot be undone.')) return;
    try {
      await contentAPI.delete(item._dbId);
      const remove = setter => setter(p => ({ ...p, [lng]: p[lng].filter(x => x._localId !== item._localId) }));
      if (sec === 'testimonials') remove(setTestimonials);
      else if (sec === 'useCases') remove(setUseCases);
      toast.success('Deleted');
    } catch (err) {
      toast.error(`Delete failed: ${err?.response?.data?.message || err.message}`);
    }
  };

  // ── field patching ────────────────────────────────────────────

  const patchListItem = (setter, lng, localId, field, value) => {
    setter(p => ({
      ...p,
      [lng]: p[lng].map(x => x._localId === localId
        ? { ...x, isDirty: true, data: { ...x.data, [field]: value } }
        : x),
    }));
  };

  const patchSingle = (setter, lng, field, value) => {
    setter(p => ({
      ...p,
      [lng]: { ...p[lng], isDirty: true, data: { ...p[lng].data, [field]: value } },
    }));
  };

  // ── add list items ────────────────────────────────────────────

  const addTestimonial = () => {
    setTestimonials(p => ({
      ...p,
      [lang]: [...(p[lang] || []), {
        _localId: uid(), _dbId: null, isDirty: true,
        key: `t_${Date.now()}`,
        data: { avatar: '😊', name: '', role: '', quote: '', label: lang === 'en' ? 'Clients' : 'Clienti' },
      }],
    }));
  };

  const addUseCase = () => {
    setUseCases(p => ({
      ...p,
      [lang]: [...(p[lang] || []), {
        _localId: uid(), _dbId: null, isDirty: true,
        key: `uc_${Date.now()}`,
        data: { icon: '🏭', title: '', problem: '', solution: '' },
      }],
    }));
  };

  // ── current data ──────────────────────────────────────────────

  const curTest  = testimonials[lang];
  const curCases = useCases[lang];
  const curHero  = hero[lang];
  const curProd  = products[lang];

  // ── render ────────────────────────────────────────────────────

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">

        {/* Page header */}
        <header className="content-header" style={{ marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <Globe size={22} color="#ec4899" />
              Content Management
            </h1>
            <p style={{ color: '#71717a', marginTop: 4 }}>
              Edit website copy in Italian and English
            </p>
          </div>
          <button className="cms-btn cms-btn-ghost" onClick={forceReload} disabled={loading} title="Reload from database">
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </header>

        {/* Language toggle */}
        <div className="cms-toolbar">
          <div className="cms-lang-toggle">
            {['it', 'en'].map(l => (
              <button
                key={l}
                className={`cms-lang-btn ${lang === l ? 'active' : ''}`}
                onClick={() => setLang(l)}
              >
                {l === 'it' ? '🇮🇹 Italiano' : '🇬🇧 English'}
              </button>
            ))}
          </div>
          <span className="cms-lang-hint">
            Editing <strong>{lang.toUpperCase()}</strong> content
          </span>
        </div>

        {/* Section tabs */}
        <div className="cms-tabs">
          {SECTIONS.map(({ key, label, Icon, color }) => {
            const count = key === 'testimonials' ? (curTest?.length || 0)
                        : key === 'useCases'     ? (curCases?.length || 0)
                        : null;
            return (
              <button
                key={key}
                className={`cms-tab ${section === key ? 'active' : ''}`}
                onClick={() => setSection(key)}
                style={section === key ? { color, borderBottomColor: color } : {}}
              >
                <Icon size={14} />
                {label}
                {count !== null && (
                  <span className="cms-tab-count"
                    style={section === key ? { background: `${color}22`, color } : {}}
                  >{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* ═════════════════════════════════════════════════════
            TESTIMONIALS
        ═════════════════════════════════════════════════════ */}
        {section === 'testimonials' && (
          <div>
            <div className="cms-section-header">
              <div>
                <h2 className="cms-section-title">Customer Testimonials</h2>
                <p className="cms-section-subtitle">Quotes shown in the social-proof section of the homepage</p>
              </div>
              <button className="cms-btn cms-btn-primary" onClick={addTestimonial}>
                <Plus size={14} /> Add Testimonial
              </button>
            </div>

            {!curTest ? (
              <div className="cms-empty"><RefreshCw size={24} className="spin" /><p>Loading…</p></div>
            ) : curTest.length === 0 ? (
              <div className="cms-empty"><MessageSquareQuote size={36} /><p>No testimonials yet.</p></div>
            ) : (
              <div className="cms-items-grid">
                {curTest.map((item, idx) => (
                  <div key={item._localId} className={`cms-item-card ${item.isDirty ? 'is-dirty' : ''}`}>
                    <div className="cms-card-top">
                      <span className="cms-card-index">
                        #{idx + 1}
                        {item.isDirty && <span className="cms-dirty-badge"><span className="cms-unsaved-dot" /> unsaved</span>}
                      </span>
                      <div className="cms-card-actions">
                        <button
                          className="cms-btn cms-btn-success cms-btn-sm"
                          onClick={() => saveItem('testimonials', lang, item)}
                          disabled={saving[item._localId] || !item.isDirty}
                        >
                          <Save size={12} />
                          {saving[item._localId] ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          className="cms-btn cms-btn-danger cms-btn-sm"
                          onClick={() => deleteItem('testimonials', lang, item)}
                          disabled={saving[item._localId]}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>

                    <div className="cms-card-form">
                      <div className="cms-form-row">
                        <div className="cms-field" style={{ width: 84, flexShrink: 0 }}>
                          <label>Avatar</label>
                          <input className="cms-input cms-emoji-input"
                            value={item.data.avatar || ''}
                            onChange={e => patchListItem(setTestimonials, lang, item._localId, 'avatar', e.target.value)}
                            placeholder="😊" maxLength={8}
                          />
                        </div>
                        <div className="cms-field" style={{ flex: 1 }}>
                          <label>Name</label>
                          <input className="cms-input"
                            value={item.data.name || ''}
                            onChange={e => patchListItem(setTestimonials, lang, item._localId, 'name', e.target.value)}
                            placeholder="Customer name"
                          />
                        </div>
                        <div className="cms-field" style={{ flex: 1 }}>
                          <label>Role / Company</label>
                          <input className="cms-input"
                            value={item.data.role || ''}
                            onChange={e => patchListItem(setTestimonials, lang, item._localId, 'role', e.target.value)}
                            placeholder="Farmer, Caserta"
                          />
                        </div>
                      </div>
                      <div className="cms-field" style={{ marginTop: 10 }}>
                        <label>Quote</label>
                        <textarea className="cms-textarea"
                          value={item.data.quote || ''}
                          onChange={e => patchListItem(setTestimonials, lang, item._localId, 'quote', e.target.value)}
                          placeholder="What the customer said…"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═════════════════════════════════════════════════════
            USE CASES
        ═════════════════════════════════════════════════════ */}
        {section === 'useCases' && (
          <div>
            <div className="cms-section-header">
              <div>
                <h2 className="cms-section-title">Use Cases</h2>
                <p className="cms-section-subtitle">Industry problem → solution cards in the "For Whom" section</p>
              </div>
              <button className="cms-btn cms-btn-primary" onClick={addUseCase}>
                <Plus size={14} /> Add Use Case
              </button>
            </div>

            {!curCases ? (
              <div className="cms-empty"><RefreshCw size={24} className="spin" /><p>Loading…</p></div>
            ) : curCases.length === 0 ? (
              <div className="cms-empty"><Factory size={36} /><p>No use cases yet.</p></div>
            ) : (
              <div className="cms-items-grid">
                {curCases.map((item, idx) => (
                  <div key={item._localId} className={`cms-item-card ${item.isDirty ? 'is-dirty' : ''}`}>
                    <div className="cms-card-top">
                      <span className="cms-card-index">
                        #{idx + 1}
                        {item.isDirty && <span className="cms-dirty-badge"><span className="cms-unsaved-dot" /> unsaved</span>}
                      </span>
                      <div className="cms-card-actions">
                        <button
                          className="cms-btn cms-btn-success cms-btn-sm"
                          onClick={() => saveItem('useCases', lang, item)}
                          disabled={saving[item._localId] || !item.isDirty}
                        >
                          <Save size={12} />
                          {saving[item._localId] ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          className="cms-btn cms-btn-danger cms-btn-sm"
                          onClick={() => deleteItem('useCases', lang, item)}
                          disabled={saving[item._localId]}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>

                    <div className="cms-card-form">
                      <div className="cms-form-row">
                        <div className="cms-field" style={{ width: 84, flexShrink: 0 }}>
                          <label>Icon</label>
                          <input className="cms-input cms-emoji-input"
                            value={item.data.icon || ''}
                            onChange={e => patchListItem(setUseCases, lang, item._localId, 'icon', e.target.value)}
                            placeholder="🏭" maxLength={8}
                          />
                        </div>
                        <div className="cms-field" style={{ flex: 1 }}>
                          <label>Title / Industry</label>
                          <input className="cms-input"
                            value={item.data.title || ''}
                            onChange={e => patchListItem(setUseCases, lang, item._localId, 'title', e.target.value)}
                            placeholder="Agriculture"
                          />
                        </div>
                      </div>
                      <div className="cms-form-row" style={{ marginTop: 10 }}>
                        <div className="cms-field">
                          <label>Problem</label>
                          <input className="cms-input"
                            value={item.data.problem || ''}
                            onChange={e => patchListItem(setUseCases, lang, item._localId, 'problem', e.target.value)}
                            placeholder="Machine down = lost harvest"
                          />
                        </div>
                        <div className="cms-field">
                          <label>Solution</label>
                          <input className="cms-input"
                            value={item.data.solution || ''}
                            onChange={e => patchListItem(setUseCases, lang, item._localId, 'solution', e.target.value)}
                            placeholder="Parts in 24h, even vintage equipment"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ═════════════════════════════════════════════════════
            HERO
        ═════════════════════════════════════════════════════ */}
        {section === 'hero' && (
          <div>
            <div className="cms-section-header">
              <div>
                <h2 className="cms-section-title">Hero Section</h2>
                <p className="cms-section-subtitle">Headline, subtitle, and CTA buttons at the top of the homepage</p>
              </div>
            </div>

            {curHero === null ? (
              <div className="cms-empty"><RefreshCw size={24} className="spin" /><p>Loading…</p></div>
            ) : curHero === false ? (
              <div className="cms-empty">
                <p>No hero content saved yet.</p>
                <button className="cms-btn cms-btn-primary" onClick={() => setHero(p => ({ ...p, [lang]: seedHero(lang) }))}>
                  ＋ Add Default Hero
                </button>
              </div>
            ) : (
              <div className={`cms-hero-form ${curHero.isDirty ? 'is-dirty' : ''}`}>
                {curHero.isDirty && (
                  <div style={{ marginBottom: 20 }}>
                    <span className="cms-dirty-badge"><span className="cms-unsaved-dot" /> Unsaved changes</span>
                  </div>
                )}

                <div className="cms-hero-group">
                  <p className="cms-hero-group-title">Badge</p>
                  <div className="cms-field">
                    <label>Badge text</label>
                    <input className="cms-input"
                      value={curHero.data.badge || ''}
                      onChange={e => patchSingle(setHero, lang, 'badge', e.target.value)}
                      placeholder="Beta Launch • Free Quote"
                    />
                  </div>
                </div>

                <div className="cms-hero-group">
                  <p className="cms-hero-group-title">Headline</p>
                  <div className="cms-form-row">
                    <div className="cms-field">
                      <label>Line 1</label>
                      <input className="cms-input"
                        value={curHero.data.title1 || ''}
                        onChange={e => patchSingle(setHero, lang, 'title1', e.target.value)}
                        placeholder="Your Part."
                      />
                    </div>
                    <div className="cms-field">
                      <label>Line 2</label>
                      <input className="cms-input"
                        value={curHero.data.title2 || ''}
                        onChange={e => patchSingle(setHero, lang, 'title2', e.target.value)}
                        placeholder="Tomorrow Morning."
                      />
                    </div>
                  </div>
                </div>

                <div className="cms-hero-group">
                  <p className="cms-hero-group-title">Subtitle</p>
                  <div className="cms-field" style={{ marginBottom: 10 }}>
                    <label>Subtitle</label>
                    <input className="cms-input"
                      value={curHero.data.subtitle || ''}
                      onChange={e => patchSingle(setHero, lang, 'subtitle', e.target.value)}
                      placeholder="Industrial 3D printing for businesses across Italy."
                    />
                  </div>
                  <div className="cms-field">
                    <label>Bold ending</label>
                    <input className="cms-input"
                      value={curHero.data.subtitleBold || ''}
                      onChange={e => patchSingle(setHero, lang, 'subtitleBold', e.target.value)}
                      placeholder=" Fast delivery guaranteed."
                    />
                  </div>
                </div>

                <div className="cms-hero-group">
                  <p className="cms-hero-group-title">Buttons</p>
                  <div className="cms-form-row">
                    <div className="cms-field">
                      <label>Primary CTA</label>
                      <input className="cms-input"
                        value={curHero.data.ctaPrimary || ''}
                        onChange={e => patchSingle(setHero, lang, 'ctaPrimary', e.target.value)}
                        placeholder="Request Free Quote"
                      />
                    </div>
                    <div className="cms-field">
                      <label>Secondary CTA</label>
                      <input className="cms-input"
                        value={curHero.data.ctaSecondary || ''}
                        onChange={e => patchSingle(setHero, lang, 'ctaSecondary', e.target.value)}
                        placeholder="Learn How"
                      />
                    </div>
                  </div>
                </div>

                <div className="cms-hero-group">
                  <p className="cms-hero-group-title">Trust Chips (3 stats below buttons)</p>
                  {[
                    { valKey: 'chip1val', labelKey: 'trust1', valPh: '24h', labelPh: lang === 'it' ? 'consegna' : 'delivery' },
                    { valKey: 'chip2val', labelKey: 'trust2', valPh: '1 pz', labelPh: lang === 'it' ? 'minimo ordine' : 'min. order' },
                    { valKey: 'chip3val', labelKey: 'trust3', valPh: '2h',   labelPh: lang === 'it' ? 'preventivo' : 'quote' },
                  ].map(({ valKey, labelKey, valPh, labelPh }, i) => (
                    <div key={valKey} style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'flex-end' }}>
                      <div className="cms-field" style={{ width: 90, flexShrink: 0 }}>
                        <label>Value {i + 1}</label>
                        <input className="cms-input"
                          value={curHero.data[valKey] || ''}
                          onChange={e => patchSingle(setHero, lang, valKey, e.target.value)}
                          placeholder={valPh}
                        />
                      </div>
                      <div className="cms-field" style={{ flex: 1 }}>
                        <label>Label {i + 1}</label>
                        <input className="cms-input"
                          value={curHero.data[labelKey] || ''}
                          onChange={e => patchSingle(setHero, lang, labelKey, e.target.value)}
                          placeholder={labelPh}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="cms-save-bar">
                  <button
                    className="cms-btn cms-btn-success"
                    onClick={() => saveItem('hero', lang, curHero)}
                    disabled={saving[curHero._localId] || !curHero.isDirty}
                  >
                    <Save size={14} />
                    {saving[curHero._localId] ? 'Saving…' : 'Save Hero'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ═════════════════════════════════════════════════════
            PRODUCTS
        ═════════════════════════════════════════════════════ */}
        {section === 'products' && (
          <div>
            <div className="cms-section-header">
              <div>
                <h2 className="cms-section-title">Products &amp; Materials</h2>
                <p className="cms-section-subtitle">Product categories, tags, and available materials</p>
              </div>
            </div>

            {curProd === null ? (
              <div className="cms-empty"><RefreshCw size={24} className="spin" /><p>Loading…</p></div>
            ) : curProd === false ? (
              <div className="cms-empty">
                <p>No products content saved yet.</p>
                <button className="cms-btn cms-btn-primary" onClick={() => setProducts(p => ({ ...p, [lang]: seedProducts(lang) }))}>
                  ＋ Add Default Products
                </button>
              </div>
            ) : (
              <div className={`cms-products-form ${curProd.isDirty ? 'is-dirty' : ''}`}>
                {curProd.isDirty && (
                  <div style={{ marginBottom: 20 }}>
                    <span className="cms-dirty-badge"><span className="cms-unsaved-dot" /> Unsaved changes</span>
                  </div>
                )}

                {/* Section labels */}
                <div className="cms-hero-group">
                  <p className="cms-hero-group-title">Section Header</p>
                  <div className="cms-field" style={{ marginBottom: 10 }}>
                    <label>Section label</label>
                    <input className="cms-input"
                      value={curProd.data.label || ''}
                      onChange={e => patchSingle(setProducts, lang, 'label', e.target.value)}
                      placeholder="What We Make"
                    />
                  </div>
                  <div className="cms-field" style={{ marginBottom: 10 }}>
                    <label>Title</label>
                    <input className="cms-input"
                      value={curProd.data.title || ''}
                      onChange={e => patchSingle(setProducts, lang, 'title', e.target.value)}
                      placeholder="If it can be printed, we do it."
                    />
                  </div>
                  <div className="cms-field">
                    <label>Subtitle</label>
                    <input className="cms-input"
                      value={curProd.data.subtitle || ''}
                      onChange={e => patchSingle(setProducts, lang, 'subtitle', e.target.value)}
                      placeholder="From precision parts to rapid prototypes…"
                    />
                  </div>
                </div>

                {/* Tags */}
                <div className="cms-hero-group">
                  <p className="cms-hero-group-title">Product Tags <span style={{ fontWeight: 400, color: '#52525b' }}>(shown as chips on the homepage)</span></p>
                  <div className="cms-tag-list">
                    {(curProd.data.tags || []).map((tag, i) => (
                      <span key={i} className="cms-tag">
                        {tag}
                        <button className="cms-tag-remove" onClick={() => {
                          patchSingle(setProducts, lang, 'tags', curProd.data.tags.filter((_, j) => j !== i));
                        }}><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                  <div className="cms-add-tag-row">
                    <input className="cms-input"
                      value={newTag}
                      onChange={e => setNewTag(e.target.value)}
                      placeholder="Add tag and press Enter…"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newTag.trim()) {
                          patchSingle(setProducts, lang, 'tags', [...(curProd.data.tags || []), newTag.trim()]);
                          setNewTag('');
                        }
                      }}
                    />
                    <button className="cms-btn cms-btn-ghost cms-btn-sm"
                      onClick={() => {
                        if (newTag.trim()) {
                          patchSingle(setProducts, lang, 'tags', [...(curProd.data.tags || []), newTag.trim()]);
                          setNewTag('');
                        }
                      }}
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                </div>

                {/* Materials */}
                <div className="cms-hero-group">
                  <p className="cms-hero-group-title">Available Materials</p>
                  <div className="cms-tag-list">
                    {(curProd.data.materialsList || []).map((mat, i) => (
                      <span key={i} className="cms-tag" style={{ borderColor: 'rgba(6,182,212,0.3)', color: '#06b6d4' }}>
                        {mat}
                        <button className="cms-tag-remove" onClick={() => {
                          patchSingle(setProducts, lang, 'materialsList', curProd.data.materialsList.filter((_, j) => j !== i));
                        }}><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                  <div className="cms-add-tag-row">
                    <input className="cms-input"
                      value={newMat}
                      onChange={e => setNewMat(e.target.value)}
                      placeholder="PLA, ABS, PETG…"
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newMat.trim()) {
                          patchSingle(setProducts, lang, 'materialsList', [...(curProd.data.materialsList || []), newMat.trim()]);
                          setNewMat('');
                        }
                      }}
                    />
                    <button className="cms-btn cms-btn-ghost cms-btn-sm"
                      onClick={() => {
                        if (newMat.trim()) {
                          patchSingle(setProducts, lang, 'materialsList', [...(curProd.data.materialsList || []), newMat.trim()]);
                          setNewMat('');
                        }
                      }}
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                </div>

                {/* Categories */}
                <div className="cms-hero-group">
                  <p className="cms-hero-group-title">Product Categories</p>
                  {(curProd.data.categories || []).length === 0 && (
                    <p style={{ color: '#52525b', fontSize: 13, marginBottom: 12 }}>No categories yet — add one below.</p>
                  )}
                  <div className="cms-categories-list">
                    {(curProd.data.categories || []).map((cat, i) => (
                      <div key={i} className="cms-category-row">
                        <input className="cms-input"
                          value={cat.name}
                          onChange={e => {
                            const next = curProd.data.categories.map((c, j) => j === i ? { ...c, name: e.target.value } : c);
                            patchSingle(setProducts, lang, 'categories', next);
                          }}
                          placeholder="Name"
                        />
                        <input className="cms-input"
                          value={cat.desc}
                          onChange={e => {
                            const next = curProd.data.categories.map((c, j) => j === i ? { ...c, desc: e.target.value } : c);
                            patchSingle(setProducts, lang, 'categories', next);
                          }}
                          placeholder="Description"
                        />
                        <button className="cms-category-del"
                          onClick={() => patchSingle(setProducts, lang, 'categories', curProd.data.categories.filter((_, j) => j !== i))}
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="cms-category-row" style={{ marginTop: 10 }}>
                    <input className="cms-input"
                      value={newCat.name}
                      onChange={e => setNewCat(p => ({ ...p, name: e.target.value }))}
                      placeholder="Category name"
                    />
                    <input className="cms-input"
                      value={newCat.desc}
                      onChange={e => setNewCat(p => ({ ...p, desc: e.target.value }))}
                      placeholder="Short description"
                    />
                    <button className="cms-btn cms-btn-ghost cms-btn-sm"
                      style={{ whiteSpace: 'nowrap' }}
                      onClick={() => {
                        if (newCat.name.trim()) {
                          patchSingle(setProducts, lang, 'categories', [...(curProd.data.categories || []), { ...newCat }]);
                          setNewCat({ name: '', desc: '' });
                        }
                      }}
                    >
                      <Plus size={12} /> Add
                    </button>
                  </div>
                </div>

                <div className="cms-save-bar">
                  <button
                    className="cms-btn cms-btn-success"
                    onClick={() => saveItem('products', lang, curProd)}
                    disabled={saving[curProd._localId] || !curProd.isDirty}
                  >
                    <Save size={14} />
                    {saving[curProd._localId] ? 'Saving…' : 'Save Products'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminContent;
